import React from 'react';
import {
  Shield,
  Trash2,
  Download,
  FileX,
  AlertTriangle,
  Clock,
  CheckCircle,
  Mail,
  Lock,
  Database,
  User,
} from 'lucide-react';

const DataSecurity = () => {
  return (
    <section id='seguranca' className='py-40 bg-white'>
      <div className='container mx-auto px-4'>
        {/* Header */}
        <div className='text-center mb-16'>
          <div className='inline-flex items-center bg-red-100 text-red-600 px-6 py-3 rounded-full font-semibold mb-6'>
            <Shield className='w-5 h-5 mr-2' />
            Segurança de Dados
          </div>

          <h2 className='text-4xl lg:text-5xl font-bold mb-6'>
            Controle total sobre
            <span className='gradient-text block'>seus dados</span>
          </h2>

          <p className='text-xl text-gray-600 max-w-3xl mx-auto'>
            No FinZiin, você tem controle absoluto sobre seus dados pessoais e
            financeiros. Saiba como solicitar exclusão parcial ou completa das
            suas informações.
          </p>
        </div>

        {/* Quick Actions */}
        <div className='grid md:grid-cols-2 gap-6 mb-16 max-w-4xl mx-auto'>
          <div className='bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center'>
            <div className='bg-yellow-500 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4'>
              <FileX className='w-8 h-8' />
            </div>
            <h3 className='text-xl font-bold mb-2 text-yellow-800'>
              Exclusão Parcial
            </h3>
            <p className='text-yellow-600 text-sm'>
              Exclua apenas movimentações mantendo a conta
            </p>
          </div>

          <div className='bg-red-50 border border-red-200 rounded-xl p-6 text-center'>
            <div className='bg-red-500 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4'>
              <Trash2 className='w-8 h-8' />
            </div>
            <h3 className='text-xl font-bold mb-2 text-red-800'>
              Exclusão Total
            </h3>
            <p className='text-red-600 text-sm'>
              Remova completamente sua conta e todos os dados
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className='max-w-4xl mx-auto'>
          {/* 1. Tipos de Exclusão */}
          <div className='bg-white rounded-2xl shadow-xl p-8 mb-8'>
            <h3 className='text-3xl font-bold mb-8 text-gray-800 flex items-center'>
              <Database className='w-8 h-8 mr-3 text-blue-500' />
              Tipos de Exclusão de Dados
            </h3>

            <div className='grid lg:grid-cols-2 gap-8'>
              {/* Exclusão Parcial */}
              <div className='border border-yellow-200 rounded-xl p-6'>
                <div className='flex items-center mb-4'>
                  <div className='bg-yellow-100 p-3 rounded-lg mr-4'>
                    <FileX className='w-6 h-6 text-yellow-600' />
                  </div>
                  <div>
                    <h4 className='text-xl font-bold text-yellow-800'>
                      Exclusão Parcial
                    </h4>
                    <p className='text-yellow-600 text-sm'>
                      Mantenha a conta, remova apenas dados específicos
                    </p>
                  </div>
                </div>

                <div className='space-y-3'>
                  <h5 className='font-semibold text-gray-800 mb-2'>
                    O que pode ser excluído:
                  </h5>
                  <div className='space-y-2'>
                    <div className='flex items-center space-x-2'>
                      <CheckCircle className='w-4 h-4 text-green-500' />
                      <span className='text-sm text-gray-600'>
                        Todas as movimentações financeiras
                      </span>
                    </div>
                    <div className='flex items-center space-x-2'>
                      <CheckCircle className='w-4 h-4 text-green-500' />
                      <span className='text-sm text-gray-600'>
                        Receitas, despesas e transferências
                      </span>
                    </div>
                    <div className='flex items-center space-x-2'>
                      <CheckCircle className='w-4 h-4 text-green-500' />
                      <span className='text-sm text-gray-600'>
                        Movimentações de cartões de crédito
                      </span>
                    </div>
                  </div>

                  <div className='bg-yellow-50 p-3 rounded-lg mt-4'>
                    <h5 className='font-semibold text-yellow-800 mb-1'>
                      O que permanece:
                    </h5>
                    <ul className='text-xs text-yellow-700 space-y-1'>
                      <li>• Contas e cartões de crédito</li>
                      <li>• Categorias e tags</li>
                      <li>• Configurações do app</li>
                      <li>• Dados da conta (nome, email)</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Exclusão Total */}
              <div className='border border-red-200 rounded-xl p-6'>
                <div className='flex items-center mb-4'>
                  <div className='bg-red-100 p-3 rounded-lg mr-4'>
                    <Trash2 className='w-6 h-6 text-red-600' />
                  </div>
                  <div>
                    <h4 className='text-xl font-bold text-red-800'>
                      Exclusão Total
                    </h4>
                    <p className='text-red-600 text-sm'>
                      Remove completamente sua conta do FinZiin
                    </p>
                  </div>
                </div>

                <div className='space-y-3'>
                  <h5 className='font-semibold text-gray-800 mb-2'>
                    O que será excluído:
                  </h5>
                  <div className='space-y-2'>
                    <div className='flex items-center space-x-2'>
                      <Trash2 className='w-4 h-4 text-red-500' />
                      <span className='text-sm text-gray-600'>
                        Todas as movimentações financeiras
                      </span>
                    </div>
                    <div className='flex items-center space-x-2'>
                      <Trash2 className='w-4 h-4 text-red-500' />
                      <span className='text-sm text-gray-600'>
                        Receitas, despesas e transferências
                      </span>
                    </div>
                    <div className='flex items-center space-x-2'>
                      <Trash2 className='w-4 h-4 text-red-500' />
                      <span className='text-sm text-gray-600'>
                        Movimentações de cartões de crédito
                      </span>
                    </div>
                    <div className='flex items-center space-x-2'>
                      <Trash2 className='w-4 h-4 text-red-500' />
                      <span className='text-sm text-gray-600'>
                        Limites de gastos
                      </span>
                    </div>
                    <div className='flex items-center space-x-2'>
                      <Trash2 className='w-4 h-4 text-red-500' />
                      <span className='text-sm text-gray-600'>
                        Metas de finanças
                      </span>
                    </div>
                    <div className='flex items-center space-x-2'>
                      <Trash2 className='w-4 h-4 text-red-500' />
                      <span className='text-sm text-gray-600'>
                        Conta de usuário completa
                      </span>
                    </div>
                  </div>

                  <div className='bg-red-50 border-l-4 border-red-400 p-3 mt-4'>
                    <div className='flex items-start space-x-2'>
                      <AlertTriangle className='w-4 h-4 text-red-600 mt-0.5' />
                      <div>
                        <h5 className='font-semibold text-red-800 text-sm'>
                          Atenção:
                        </h5>
                        <p className='text-xs text-red-700'>
                          Esta ação é irreversível. Todos os dados serão
                          permanentemente removidos.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 2. Como Solicitar */}
          <div className='bg-gradient-to-r from-blue-50 to-emerald-50 rounded-2xl p-8 mb-8'>
            <h3 className='text-3xl font-bold mb-8 text-gray-800 flex items-center'>
              <Mail className='w-8 h-8 mr-3 text-emerald-500' />
              Como Solicitar Exclusão de Dados
            </h3>

            <div className='grid lg:grid-cols-2 gap-8'>
              {/* Método 1: Pelo App */}
              <div className='bg-white rounded-xl p-6 shadow-lg'>
                <h4 className='text-xl font-bold mb-4 text-gray-800 flex items-center'>
                  <Trash2 className='w-5 h-5 mr-2 text-blue-500' />
                  Pelo Aplicativo
                </h4>

                <div className='space-y-3'>
                  <div className='flex items-start space-x-3'>
                    <div className='bg-blue-100 text-blue-600 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold'>
                      1
                    </div>
                    <div>
                      <p className='font-medium text-gray-800'>
                        Acesse Configurações
                      </p>
                      <p className='text-sm text-gray-600'>
                        Menu {'>'} Configurações
                      </p>
                    </div>
                  </div>

                  <div className='flex items-start space-x-3'>
                    <div className='bg-blue-100 text-blue-600 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold'>
                      2
                    </div>
                    <div>
                      <p className='font-medium text-gray-800'>
                        Escolha o tipo de exclusão
                      </p>
                      <p className='text-sm text-gray-600'>
                        Parcial (dados) ou Total (conta)
                      </p>
                    </div>
                  </div>

                  <div className='flex items-start space-x-3'>
                    <div className='bg-blue-100 text-blue-600 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold'>
                      3
                    </div>
                    <div>
                      <p className='font-medium text-gray-800'>
                        Confirme a solicitação
                      </p>
                      <p className='text-sm text-gray-600'>
                        Digite a palavra-chave solicitada para confirmar
                      </p>
                    </div>
                  </div>
                </div>

                <div className='mt-4 p-3 bg-blue-50 rounded-lg'>
                  <p className='text-blue-800 text-sm'>
                    <strong>Processamento:</strong> Exclusões são processadas
                    imediatamente para todos os dados.
                  </p>
                </div>
              </div>

              {/* Método 2: Por Email */}
              <div className='bg-white rounded-xl p-6 shadow-lg'>
                <h4 className='text-xl font-bold mb-4 text-gray-800 flex items-center'>
                  <Mail className='w-5 h-5 mr-2 text-emerald-500' />
                  Por Email
                </h4>

                <div className='space-y-4'>
                  <div className='bg-emerald-50 p-4 rounded-lg'>
                    <p className='font-semibold text-emerald-800 mb-2'>
                      Envie para:
                    </p>
                    <p className='text-emerald-700 font-mono text-sm'>
                      fin​ziin.app@gmail.com
                    </p>
                  </div>

                  <div>
                    <p className='font-semibold text-gray-800 mb-2'>
                      Inclua as seguintes informações:
                    </p>
                    <ul className='list-disc list-inside space-y-1 text-sm text-gray-600'>
                      <li>Email ou telefone da conta registrada no FinZiin</li>
                      <li>Tipo de exclusão desejada (parcial ou total)</li>
                      <li>Se parcial, especifique quais dados</li>
                      <li>Motivo da solicitação (opcional)</li>
                      <li>Confirmação: "Solicito a exclusão dos meus dados"</li>
                    </ul>
                  </div>

                  <div className='bg-yellow-50 p-3 rounded-lg'>
                    <div className='flex items-start space-x-2'>
                      <Clock className='w-4 h-4 text-yellow-600 mt-0.5' />
                      <div>
                        <p className='font-semibold text-yellow-800 text-sm'>
                          Tempo de resposta:
                        </p>
                        <p className='text-yellow-700 text-xs'>
                          Até 72 horas para confirmação. Processamento em até 30
                          dias.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 3. Processo e Prazos */}
          <div className='bg-white rounded-2xl shadow-xl p-8 mb-8'>
            <h3 className='text-3xl font-bold mb-8 text-gray-800 flex items-center'>
              <Clock className='w-8 h-8 mr-3 text-purple-500' />
              Processo e Prazos
            </h3>

            <div className='space-y-6'>
              {/* Timeline */}
              <div className='relative'>
                <div className='absolute left-4 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-500 to-emerald-500'></div>

                <div className='space-y-8'>
                  <div className='flex items-start space-x-4'>
                    <div className='bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm relative z-10'>
                      1
                    </div>
                    <div>
                      <h4 className='font-bold text-gray-800'>
                        Recebimento da Solicitação
                      </h4>
                      <p className='text-gray-600 text-sm'>
                        Confirmamos o recebimento em até 24 horas
                      </p>
                      <div className='text-xs text-blue-600 mt-1'>
                        📧 Email de confirmação
                      </div>
                    </div>
                  </div>

                  <div className='flex items-start space-x-4'>
                    <div className='bg-yellow-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm relative z-10'>
                      2
                    </div>
                    <div>
                      <h4 className='font-bold text-gray-800'>
                        Verificação de Identidade
                      </h4>
                      <p className='text-gray-600 text-sm'>
                        Validamos se você é o titular da conta
                      </p>
                      <div className='text-xs text-yellow-600 mt-1'>
                        🔍 Pode levar até 48 horas
                      </div>
                    </div>
                  </div>

                  <div className='flex items-start space-x-4'>
                    <div className='bg-purple-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm relative z-10'>
                      3
                    </div>
                    <div>
                      <h4 className='font-bold text-gray-800'>Processamento</h4>
                      <p className='text-gray-600 text-sm'>
                        Executamos a exclusão conforme solicitado
                      </p>
                      <div className='text-xs text-purple-600 mt-1'>
                        ⚡ Dados financeiros: Imediato | Conta completa: Até 30
                        dias
                      </div>
                    </div>
                  </div>

                  <div className='flex items-start space-x-4'>
                    <div className='bg-emerald-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm relative z-10'>
                      4
                    </div>
                    <div>
                      <h4 className='font-bold text-gray-800'>
                        Confirmação Final
                      </h4>
                      <p className='text-gray-600 text-sm'>
                        Enviamos confirmação da conclusão
                      </p>
                      <div className='text-xs text-emerald-600 mt-1'>
                        ✅ Email com detalhes do que foi excluído
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact CTA */}
        <div className='text-center mt-16'>
          <div className='bg-gray-900 text-white rounded-2xl p-8 max-w-2xl mx-auto'>
            <h3 className='text-2xl font-bold mb-4'>Precisa de ajuda?</h3>
            <p className='text-gray-300 mb-6'>
              Nossa equipe está disponível para esclarecer dúvidas sobre
              segurança de dados e processos de exclusão.
            </p>
            <div className='flex flex-col sm:flex-row gap-4 justify-center'>
              <a
                href='mailto:finziin.app@gmail.com'
                className='inline-flex items-center bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-300'
              >
                <Mail className='w-5 h-5 mr-2' />
                Contatar Privacidade
              </a>
              <a
                href='mailto:finziin.app@gmail.com'
                className='inline-flex items-center bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-300'
              >
                <User className='w-5 h-5 mr-2' />
                Suporte Geral
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DataSecurity;
